/**
 * §7.2 主管的落點：`/overview` **站台狀態**。
 * 「各站進行中工單、無法結算的工單、異常」——這是主管一早開機要看的那一頁。
 */

import { useQuery } from '../api/hooks.js'
import { Alert, Card, Empty, Mono, Pill, Table, Td } from '../components/ui.js'
import { useDict } from '../config/ConfigContext.js'
import { count, tsShort } from '../../../shared/lib/format.js'
import { Link } from '../router.js'

interface WorkOrderRow {
  work_order_id: string
  station_id: string
  resource_id: string
  planned_count: number
  model_code: string | null
  operator_id: string
  opened_at: string
  closed_at: string | null
  status: 'open' | 'closed'
  issue_lines: string
  receipt_lines: string
  /** 有任何一項沒有重量 → 這張工單目前算不出損耗（§9 規則 11） */
  has_missing_weight: boolean
}

export function StationOverview() {
  const dict = useDict()
  const open = useQuery<{ items: WorkOrderRow[] }>('/api/work-orders?status=open&limit=200')
  const recent = useQuery<{ items: WorkOrderRow[] }>('/api/work-orders?limit=30')

  const openItems = open.data?.items ?? []
  const blocked = (recent.data?.items ?? []).filter((w) => w.has_missing_weight)

  return (
    <div className="space-y-4">
      <Card title="進行中的工單" subtitle={`${count(openItems.length)} 張`}>
        {open.loading && <p className="text-sm text-ink-500">載入中…</p>}
        {open.error && <Alert tone="danger" title="讀不到工單">{open.error.message}</Alert>}
        {!open.loading && openItems.length === 0 && <Empty>目前沒有進行中的工單。</Empty>}
        {openItems.length > 0 && (
          <Table head={['工單', '站台', '設備', '操作員', '預計', '領料', '繳庫', '開立']}>
            {openItems.map((w) => (
              <tr key={w.work_order_id} className="hover:bg-ink-50">
                <Td>
                  <Link to={`/balance/${w.work_order_id}`} className="text-sky-700">
                    <Mono>{w.work_order_id}</Mono>
                  </Link>
                </Td>
                <Td>{dict.station(w.station_id)}</Td>
                <Td className="text-xs">{dict.resource(w.resource_id)}</Td>
                <Td className="text-xs">{w.operator_id}</Td>
                <Td className="tabular-nums text-xs">{count(w.planned_count)}</Td>
                <Td className="tabular-nums text-xs">{w.issue_lines}</Td>
                <Td className="tabular-nums text-xs">{w.receipt_lines}</Td>
                <Td className="text-xs">{tsShort(w.opened_at)}</Td>
              </tr>
            ))}
          </Table>
        )}
      </Card>

      {/* §9 規則 11：無法結算的工單要浮出來，否則會一直掛著沒人管 */}
      <Card
        title="目前算不出損耗的工單"
        subtitle="有量測項還沒有重量。不是錯誤，但要有人決定怎麼處理"
        tone={blocked.length > 0 ? 'warn' : 'plain'}
      >
        {blocked.length === 0
          ? <Empty>最近的工單都算得出來。</Empty>
          : (
            <Table head={['工單', '站台', '狀態', '開立']}>
              {blocked.map((w) => (
                <tr key={w.work_order_id}>
                  <Td>
                    <Link to={`/balance/${w.work_order_id}`} className="text-sky-700">
                      <Mono>{w.work_order_id}</Mono>
                    </Link>
                  </Td>
                  <Td>{dict.station(w.station_id)}</Td>
                  <Td><Pill tone={w.status === 'open' ? 'sky' : 'ink'}>
                    {w.status === 'open' ? '進行中' : '已關閉'}
                  </Pill></Td>
                  <Td className="text-xs">{tsShort(w.opened_at)}</Td>
                </tr>
              ))}
            </Table>
          )}
        {blocked.length > 0 && (
          <p className="mt-3 text-xs text-ink-500">
            點進去會看到每一項的原因、影響與解法。多數情況是量測點停用——啟用後補秤就能結算。
          </p>
        )}
      </Card>

      <Card title="最近關閉的工單">
        {(recent.data?.items ?? []).filter((w) => w.status === 'closed').length === 0
          ? <Empty>—</Empty>
          : (
            <Table head={['工單', '站台', '關閉時間', '損耗可算？']} dense>
              {(recent.data?.items ?? []).filter((w) => w.status === 'closed').slice(0, 15).map((w) => (
                <tr key={w.work_order_id}>
                  <Td>
                    <Link to={`/balance/${w.work_order_id}`} className="text-sky-700">
                      <Mono>{w.work_order_id}</Mono>
                    </Link>
                  </Td>
                  <Td>{dict.station(w.station_id)}</Td>
                  <Td className="text-xs">{w.closed_at ? tsShort(w.closed_at) : '—'}</Td>
                  <Td>{w.has_missing_weight
                    ? <Pill tone="amber">缺重量</Pill>
                    : <Pill tone="emerald">可結算</Pill>}</Td>
                </tr>
              ))}
            </Table>
          )}
      </Card>
    </div>
  )
}
